/**
 * Utilitário centralizado para traduzir erros técnicos em mensagens
 * amigáveis em Português do Brasil (PT-BR).
 *
 * Regra de ouro: NUNCA expor `error.message` bruto ao usuário final.
 */
import { ZodError } from 'zod';

/** Mapeamento de códigos PostgreSQL conhecidos */
const PG_CODE_MAP: Record<string, string> = {
  '23505': 'Este registro já existe no sistema.',
  '23503': 'Este registro está vinculado a outros dados e não pode ser alterado.',
  '42501': 'Você não possui permissão para realizar esta ação. Verifique se está logado.',
  '42P01': 'Ops! Algo deu errado internamente. Tente novamente ou contate o suporte.',
  '23514': '', // check_violation — tratado separadamente para preservar RAISE
  'P0001': '', // raise_exception — preserva mensagem se amigável
  'P0002': '', // no_data_found — tratado separadamente (preserva RAISE em PT-BR)
};


/** Padrões de texto que indicam erros de rede */
const NETWORK_PATTERNS = [
  'failed to fetch',
  'networkerror',
  'fetcherror',
  'load failed',
  'err_internet_disconnected',
  'net::err_',
];

/** Padrões de timeout */
const TIMEOUT_PATTERNS = ['timeout', 'aborterror', 'aborted'];

/**
 * Extrai a mensagem bruta de qualquer formato de erro.
 */
function extrairMensagem(err: unknown): string {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    const e = err as any;
    // Supabase retorna { message, code, details, hint }
    return e.message || e.error_description || e.msg || e.details || '';
  }
  return String(err);
}

/**
 * Extrai o código PostgreSQL do erro, se existir.
 */
function extrairCodigoPg(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as any;
  // Supabase PostgrestError: { code: "23505", ... }
  if (e.code && typeof e.code === 'string' && /^[A-Z0-9]{5}$/.test(e.code)) return e.code;
  // PostgREST error codes
  if (e.code && typeof e.code === 'string' && e.code.startsWith('PGRST')) return e.code;
  return null;
}

/**
 * Verifica se a mensagem parece estar em PT-BR (provavelmente vinda de RAISE EXCEPTION).
 * Heurística: contém acentos ou palavras comuns em PT-BR.
 */
function parecePortugues(msg: string): boolean {
  return /[àáâãéêíóôõúüçÀÁÂÃÉÊÍÓÔÕÚÜÇ]/.test(msg) ||
    /\b(não|já existe|conflito|horário|obrigatório|permitido|cadastrado)\b/i.test(msg);
}

/**
 * Traduz um erro técnico para uma mensagem amigável em PT-BR.
 *
 * @param err - Qualquer tipo de erro (Error, PostgrestError, string, etc.)
 * @param contexto - Contexto opcional (ex: "salvar evento") para enriquecer a msg
 * @returns Mensagem amigável para exibir ao usuário
 */
export function traduzirErro(err: unknown, contexto?: string): string {
  // 0. ZodError — validação client-side antes do RPC
  if (err instanceof ZodError) {
    return traduzirZodError(err, contexto);
  }

  const mensagemBruta = extrairMensagem(err);
  const mensagemLower = mensagemBruta.toLowerCase();
  const codigoPg = extrairCodigoPg(err);

  // 1. Código PG conhecido
  if (codigoPg) {

    // PGRST116 — registro não encontrado
    if (codigoPg === 'PGRST116') {
      return 'Não conseguimos localizar a informação solicitada.';
    }

    // check_violation (23514), raise_exception (P0001) ou no_data_found (P0002)
    // — preservar RAISE se em PT-BR
    if (codigoPg === '23514' || codigoPg === 'P0001' || codigoPg === 'P0002') {
      if (mensagemBruta && parecePortugues(mensagemBruta)) {
        // Limpeza de prefixos comuns de erro do Postgres
        let msg = mensagemBruta.replace(/^error:\s*/i, '');

        // Mapeamentos específicos para Reservas de Laboratório
        if (msg.includes('liberada para uso') && msg.includes('preparação')) {
          return 'A reserva só pode ser liberada para uso quando estiver em preparação.';
        }
        if (msg.includes('alterar o status') && msg.includes('concluída')) {
          return 'Esta reserva já foi finalizada e não pode mais ter o status alterado.';
        }
        if (msg.includes('alterar o status') && msg.includes('cancelada')) {
          return 'Esta reserva foi cancelada e não pode mais ter o status alterado.';
        }
        if (msg.includes('preparação só pode ser iniciada')) {
          return 'A preparação só pode ser iniciada se a reserva estiver aprovada.';
        }

        return msg;
      }
      return contexto
        ? `Não foi possível ${contexto}. Verifique as regras de validação.`
        : 'A operação não atende às regras de validação do sistema.';
    }


    // RLS violation
    if (codigoPg === '42501') {
      return 'Você não possui permissão para realizar esta ação. Verifique se está logado.';
    }

    const msgMapeada = PG_CODE_MAP[codigoPg];
    if (msgMapeada) return msgMapeada;
  }

  // 2. RLS na mensagem (sem código formal)
  if (mensagemLower.includes('row-level security') || mensagemLower.includes('rls')) {
    return 'Você não possui permissão para realizar esta ação. Verifique se está logado.';
  }

  // 3. Conflitos de unique que contêm nome da constraint
  if (mensagemLower.includes('duplicate key') || mensagemLower.includes('already exists')) {
    // Patrimônio duplicado em equipamentos (índice único parcial por unidade)
    if (
      mensagemLower.includes('uni_patrimonio_tenant_ativo') ||
      mensagemLower.includes('uni_patrimonio_tenant') ||
      mensagemLower.includes('uni_numero_patrimonio') ||
      mensagemLower.includes('numero_patrimonio')
    ) {
      return 'Já existe um equipamento ativo com este número de patrimônio nesta unidade. Use um número diferente.';
    }
    // Alocação duplicada de equipamento em laboratório
    if (mensagemLower.includes('uni_equipamento_local_ativo')) {
      return 'Este equipamento já está alocado neste local.';
    }
    // Preservar se veio em PT-BR do banco
    if (parecePortugues(mensagemBruta)) return mensagemBruta;
    return 'Este registro já existe no sistema.';
  }

  // 4. FK violation na mensagem
  if (mensagemLower.includes('foreign key') || mensagemLower.includes('violates foreign key')) {
    return 'Este registro está vinculado a outros dados e não pode ser alterado.';
  }

  // 5. Credenciais inválidas (auth)
  if (mensagemLower.includes('invalid login credentials') || mensagemLower.includes('invalid credentials')) {
    return 'E-mail ou senha incorretos. Verifique os dados e tente novamente.';
  }

  // 6. E-mail já registrado
  if (mensagemLower.includes('user already registered') || mensagemLower.includes('email already')) {
    return 'Este e-mail já está cadastrado no sistema.';
  }

  // 7. Erros de rede
  if (NETWORK_PATTERNS.some(p => mensagemLower.includes(p))) {
    return 'Parece que você está sem internet. Verifique sua conexão e tente novamente.';
  }

  // 8a. Cancelamento explícito pelo usuário (precede o branch de timeout
  // porque a mensagem contém ambos "aborted" e "cancelada")
  if (mensagemLower.includes('cancelada pelo usuário') || mensagemLower.includes('operação cancelada')) {
    return 'Operação cancelada.';
  }

  // 8. Timeout
  if (TIMEOUT_PATTERNS.some(p => mensagemLower.includes(p))) {
    return 'A operação demorou mais que o esperado. Tente novamente em instantes.';
  }


  // 9. Limite de requisições (rate limit)
  if (mensagemLower.includes('429') || mensagemLower.includes('rate limit') || mensagemLower.includes('too many requests')) {
    return 'Muitas tentativas em pouco tempo. Aguarde um momento e tente novamente.';
  }

  // 10. Créditos insuficientes
  if (mensagemLower.includes('402') || mensagemLower.includes('insufficient credits')) {
    return 'Créditos insuficientes. Contate o administrador do sistema.';
  }

  // 11. Mensagem já em PT-BR vinda de RAISE EXCEPTION — preservar
  if (mensagemBruta && parecePortugues(mensagemBruta) && mensagemBruta.length < 300) {
    return mensagemBruta;
  }

  // 12. Fallback genérico
  return contexto
    ? `Não foi possível ${contexto}. Tente novamente ou contate o suporte.`
    : 'Ops! Algo deu errado. Tente novamente ou contate o suporte.';
}

/**
 * Rótulos PT-BR para os campos do payload de onboarding do professor.
 * Centralizado aqui para que erros do Zod e do RPC compartilhem o mesmo dicionário.
 */
const CAMPO_LABELS: Record<string, string> = {
  p_professor_id: 'Identificador do professor',
  // pessoas
  nome_completo: 'Nome completo',
  nome_social: 'Nome social',
  data_nascimento: 'Data de nascimento',
  sexo: 'Sexo',
  estado_civil: 'Estado civil',
  nome_mae: 'Nome da mãe',
  nome_pai: 'Nome do pai',
  raca_id: 'Raça/Cor',
  nacionalidade_id: 'Nacionalidade',
  pais_origem_id: 'País de origem',
  municipio_nascimento_id: 'Município de nascimento',
  telefone_principal: 'Telefone',
  deficiencia_flag: 'Possui deficiência',
  // professores
  matricula: 'Matrícula',
  titulacao_id: 'Titulação',
  regime_trabalho_id: 'Regime de trabalho',
  area_formacao_id: 'Área de formação',
  carga_horaria_semanal: 'Carga horária semanal',
  data_admissao: 'Data de admissão',
  atuacao_ensino: 'Atuação em ensino',
  atuacao_pesquisa: 'Atuação em pesquisa',
  atuacao_extensao: 'Atuação em extensão',
  atuacao_gestao: 'Atuação em gestão',
  atuacao_conteudista: 'Atuação como conteudista',
  bolsa_pesquisa: 'Bolsa de pesquisa',
  bolsa_extensao: 'Bolsa de extensão',
  docente_visitante: 'Docente visitante',
  docente_substituto: 'Docente substituto',
  conteudista_contrato_especifico: 'Contrato específico de conteudista',
  tipo_vinculo_conteudista: 'Tipo de vínculo conteudista',
  tipo_consorcio: 'Tipo de consórcio',
  tipo_consorcio_conteudista: 'Tipo de consórcio conteudista',
  tipo_vinculo: 'Tipo de vínculo',
  orcid: 'ORCID',
  // pessoa_documentos
  identidade_numero: 'RG',
  identidade_orgao_emissor: 'Órgão emissor do RG',
  passaporte: 'Passaporte',
};

const SECAO_LABELS: Record<string, string> = {
  p_campos_pessoas: 'Dados pessoais',
  p_campos_professores: 'Dados profissionais',
  p_campos_documentos: 'Documentos',
};

function rotuloPath(path: (string | number)[]): string {
  if (!path.length) return 'Cadastro';
  const last = String(path[path.length - 1]);
  const root = String(path[0]);
  const campo = CAMPO_LABELS[last] ?? last;
  const secao = SECAO_LABELS[root];
  return secao ? `${secao} › ${campo}` : campo;
}

/** Traduz um ZodError para uma mensagem amigável em PT-BR (até 4 issues). */
function traduzirZodError(err: ZodError, contexto?: string): string {
  const issues = err.errors.slice(0, 4).map((e) => `${rotuloPath(e.path)}: ${e.message}`);
  const extras = err.errors.length > 4 ? ` (+${err.errors.length - 4} outros)` : '';
  const prefixo = contexto ? `Não foi possível ${contexto}. ` : 'Revise os dados do cadastro: ';
  return `${prefixo}${issues.join('; ')}${extras}`;
}
